import { getExtensionsManager } from '../../extensions/extensions-manager'
import { isGlobalSearchCategory, supportsGlobalSearch } from '../../extensions/utils'

import type {
  GlobalSearchActionUI,
  GlobalSearchCategoryUI,
  GlobalSearchManager,
  GlobalSearchQueryUI,
  GlobalSearchResultPreview,
  GlobalSearchResultItem,
  GlobalSearchResponse,
  GlobalSearchParams,
  GlobalSearchResponseJSON,
} from './global-search-models'
import type {
  GlobalSearchAction,
  GlobalSearchCategoryColumn,
  GlobalSearchContext,
  GlobalSearchEmptyState,
  GlobalSearchQuery,
  GlobalSearchShellExtension,
  GlobalSearchValueField,
} from '../../extensions'
import { isCommandHandled } from '../../services/namespaces'
import { getShellLogger } from '../../common/logger'
import { getLocationPathname, getWindow } from '../../common/dom-helpers'
import { onWindowUnload } from '../../common/helpers/window'
import { SEARCH_ROUTE } from '../../common/routes'
import { getCurrentSearchQuery, handleApiResponse, parseResultData } from './helpers'
import { notifyResultPageQueryChange, setupGlobalSearchCommands } from './namespace'
import { authenticatedFetch } from '../../services/auth'
import { getJiveApiBaseUrl } from '../../core/environment'
import { isAbortError } from '../../http-utils/utils'
import { convertObjectToQueryString } from '../../common'
import { getLaunchDarklyFlag } from '../../services/feature-flags'
import { LaunchDarklyVariations } from '../../services/feature-flags/models'
import { GLOBAL_SEARCH_MOCK_RESPONSE } from './mock-data'

class GlobalSearchManagerImpl implements GlobalSearchManager {
  private categoriesMapInstance: Map<string, GlobalSearchCategoryUI> | undefined = undefined
  private globalSearchExtensions: readonly GlobalSearchShellExtension[] | undefined = undefined
  private abortController: AbortController | undefined = undefined

  constructor() {
    setupGlobalSearchCommands()
    const w = getWindow()
    w.addEventListener('popstate', this.handleWindowPopstate)
    onWindowUnload(() => {
      w.removeEventListener('popstate', this.handleWindowPopstate)
    })
  }

  private handleWindowPopstate = () => {
    if (getLocationPathname().startsWith(SEARCH_ROUTE)) {
      const query = getCurrentSearchQuery()
      notifyResultPageQueryChange({ query })
    }
  }

  private getGlobalSearchExtensions = (): readonly GlobalSearchShellExtension[] => {
    if (!this.globalSearchExtensions) {
      const result: GlobalSearchShellExtension[] = []

      getExtensionsManager()
        .getExtensions()
        .forEach(extension => {
          if (supportsGlobalSearch(extension)) {
            result.push(extension)
          }
        })
      this.globalSearchExtensions = result
    }
    return this.globalSearchExtensions
  }

  public getCategories = () => Array.from(this.getCategoriesMap().values())

  public getCategoryById = (id: string) => this.getCategoriesMap().get(id)

  public getCategoryByApplication = (application: string) => this.getCategoriesMap().get(application)

  private getResultApplication = (result: GlobalSearchResultItem) => {
    const extensions = this.getGlobalSearchExtensions()
    for (const extension of extensions) {
      const application = extension.getGlobalSearchResultApplication(result)
      if (application) {
        return application
      }
    }
  }

  public getCategory = (result: GlobalSearchResultItem) => {
    const application = this.getResultApplication(result)
    if (application) {
      return this.getCategoryByApplication(application)
    }
  }

  public getGlobalSearchResultActions = (searchResult: GlobalSearchResultItem, context: GlobalSearchContext) => {
    const searchResultActionsUI: GlobalSearchActionUI[] = []
    this.getGlobalSearchExtensions().forEach(extension => {
      extension.getGlobalSearchResultActions(searchResult, context)?.forEach(action => {
        if (this.canAddActionTo(action, searchResultActionsUI)) {
          searchResultActionsUI.push(this.convertActionToUI(action, extension))
        }
      })
    })
    return searchResultActionsUI
  }

  private convertActionToUI = (
    { displayNameKey, ...rest }: GlobalSearchAction,
    extension: GlobalSearchShellExtension,
  ) => ({
    ...rest,
    displayName: extension.getString(displayNameKey),
  })

  public getResultDefaultAction = (result: GlobalSearchResultItem, context: GlobalSearchContext) => {
    const extensions = this.getGlobalSearchExtensions()
    for (const extension of extensions) {
      const defaultAction = extension.getGlobalSearchResultDefaultAction(result, context)
      if (defaultAction) {
        return defaultAction
      }
    }
  }

  public convertToGlobalSearchResultPreview = (result: GlobalSearchResultItem) => {
    const extensions = this.getGlobalSearchExtensions()

    return extensions.reduce(
      (uiResult: GlobalSearchResultPreview, extension) => {
        const application = extension.getGlobalSearchResultApplication(result)

        let defaultActionUI
        if (!uiResult.defaultAction) {
          const defaultAction = extension.getGlobalSearchResultDefaultAction(result, 'preview')
          if (defaultAction) {
            defaultActionUI = this.convertActionToUI(defaultAction, extension)
          }
        }

        if (application) {
          const category = this.getCategoryByApplication(application)
          const title = this.getResultFieldValue(result, category?.titleField) ?? ''
          const preview = this.getResultFieldValue(result, category?.previewField) ?? ''
          const userKey = this.getResultFieldValue(result, category?.avatarFields?.userKey) ?? ''
          const givenName = this.getResultFieldValue(result, category?.avatarFields?.givenName) ?? ''
          const familyName = this.getResultFieldValue(result, category?.avatarFields?.familyName) ?? ''
          const icon = this.getResultFieldValue(result, category?.iconField) ?? ''

          uiResult = {
            ...uiResult,
            title: uiResult.title || title,
            preview: uiResult.preview || preview,
            userKey: uiResult.userKey !== '' ? uiResult.userKey : userKey,
            givenName: uiResult.givenName !== '' ? uiResult.givenName : givenName,
            familyName: uiResult.familyName !== '' ? uiResult.familyName : familyName,
            icon: uiResult.icon !== '' ? uiResult.icon : icon,
            originalResult: uiResult.originalResult,
            defaultAction: uiResult.defaultAction ?? defaultActionUI,
          }
        }
        const extensionActions = extension.getGlobalSearchResultActions(result, 'preview')

        if (extensionActions) {
          const handledActions: GlobalSearchActionUI[] = []

          extensionActions.forEach(action => {
            if (this.canAddActionTo(action, uiResult.actions)) {
              handledActions.push(this.convertActionToUI(action, extension))
            }
          })
          uiResult = { ...uiResult, actions: [...uiResult.actions, ...handledActions] }
        }
        return uiResult
      },
      {
        title: '',
        preview: '',
        actions: [],
        userKey: '',
        givenName: '',
        familyName: '',
        icon: '',
        originalResult: result,
        defaultAction: undefined,
      } as GlobalSearchResultPreview,
    )
  }

  public convertToGlobalSearchResultPreviews = (
    results: readonly GlobalSearchResultItem[],
  ): readonly GlobalSearchResultPreview[] => results?.map(result => this.convertToGlobalSearchResultPreview(result))

  public searchFor = async (
    searchQuery: GlobalSearchQueryUI,
    params?: GlobalSearchParams,
  ): Promise<GlobalSearchResponse | undefined> => {
    const searchString = searchQuery.query
    const queries = this.getQueriesForSearchString(searchString)
    if (queries.length) {
      console.log('Search Queries', queries)
    }

    // This is a temporary condition to be able to test
    if (getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_GLOBAL_SEARCH_MOCK_API_RESPONSE)) {
      return GLOBAL_SEARCH_MOCK_RESPONSE
    }

    if (this.getCategories().length) {
      return await this.queryGlobalSearchAPI(queries, params).then(parseResultData)
    }
    return await Promise.resolve(undefined)
  }

  private queryGlobalSearchAPI = async (
    queries: GlobalSearchQuery[],
    params?: GlobalSearchParams,
  ): Promise<GlobalSearchResponseJSON | undefined> => {
    let data
    const stringParams = params ? '?' + convertObjectToQueryString(params) : ''

    try {
      this.abortPreviousRequest()
      const response = await authenticatedFetch<GlobalSearchResponseJSON>(
        `${getJiveApiBaseUrl()}/global-search/v1/search${stringParams}`,
        {
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
          credentials: undefined,
          signal: this.abortController?.signal,
          body: JSON.stringify({
            queries,
          }),
        },
      ).then(handleApiResponse)
      data = await response?.json()
    } catch (e) {
      if (isAbortError(e)) {
        //Silently abort
      } else {
        getShellLogger().error(`No search results could be found, ${e}`)
      }
    }
    return data
  }

  private abortPreviousRequest() {
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()
  }

  public getResultFieldValue = (result: GlobalSearchResultItem, field?: GlobalSearchValueField | undefined) => {
    if (field) {
      if (typeof field === 'string') {
        return result.data[field] as string
      }
      return field(result)
    }
  }

  private getCategoriesMap = () => {
    if (!this.categoriesMapInstance) {
      const result = new Map<string, GlobalSearchCategoryUI>()
      this.getGlobalSearchExtensions().forEach(extension => {
        extension.getGlobalSearchCategories().forEach(category => {
          if (isGlobalSearchCategory(category)) {
            const columnsUI = category.columns.map(column => this.convertColumnToUI(column, extension))
            const categoryUI = {
              ...category,
              columns: columnsUI,
              emptyState: this.convertEmptyStateToUI(category.emptyState, extension),
            }
            result.set(category.application, categoryUI)
          } else {
            getShellLogger().error(
              `Extension ${extension.id} returned a category that do not implement interface correctly`,
              category,
            )
          }
        })
      })
      this.categoriesMapInstance = result
    }
    return this.categoriesMapInstance
  }

  private convertColumnToUI = (
    { displayNameKey, ...rest }: GlobalSearchCategoryColumn,
    extension: GlobalSearchShellExtension,
  ) => ({
    ...rest,
    displayName: extension.getString(displayNameKey),
  })

  private convertEmptyStateToUI = (
    { messageKey, actions }: GlobalSearchEmptyState,
    extension: GlobalSearchShellExtension,
  ) => ({
    message: extension.getString(messageKey),
    actions: actions.map(action => this.convertActionToUI(action, extension)),
  })

  private canAddActionTo(
    action: GlobalSearchAction,
    actionList: GlobalSearchActionUI[] | readonly GlobalSearchActionUI[],
  ): boolean {
    return isCommandHandled(action.command) && !actionList.find(existingAction => existingAction.id === action.id)
  }

  public getActionsForSearchString = (
    searchString: string,
    context: GlobalSearchContext,
  ): readonly GlobalSearchActionUI[] | undefined => {
    const extensions = this.getGlobalSearchExtensions()
    const result: GlobalSearchActionUI[] = extensions.reduce((actions: GlobalSearchActionUI[], extension) => {
      const extensionActions = extension.getActionsForSearchString(searchString, context)
      if (extensionActions) {
        extensionActions.forEach(action => {
          if (this.canAddActionTo(action, actions)) {
            actions.push(this.convertActionToUI(action, extension))
          }
        })
      }
      return actions
    }, [] as GlobalSearchActionUI[])

    return result
  }

  public getQueriesForSearchString = (searchString: string): GlobalSearchQuery[] => {
    const categories = this.getCategories()
    return categories.map(category => category.getQueryForSearchString(searchString)).filter(query => query)
  }
}
let globalSearchManagerInstance: GlobalSearchManager

export const getGlobalSearchManager = (): GlobalSearchManager => {
  if (!globalSearchManagerInstance) {
    globalSearchManagerInstance = new GlobalSearchManagerImpl()
  }
  return globalSearchManagerInstance
}
